import React, { createContext, useContext, useMemo } from 'react';

import invariant from 'tiny-invariant';

import type { UserSideNavDataStore } from '../../../common/types';

import type { UserSideNavDataStoreProviderProps } from './types';

export const UserSideNavDataStoreContext = createContext<UserSideNavDataStore | undefined>(
	undefined,
);

/**
 * Stores the user side nav data and makes it available to child components.
 * This component exists so that it is easy for child components to read and update the user side nav data independently of the user menu data.
 */
export function UserSideNavDataStoreProvider({
	children,
	setUserSideNavData,
	userSideNavData,
}: UserSideNavDataStoreProviderProps) {
	const userSideNavDataStore = useMemo(
		() => ({
			setUserSideNavData,
			userSideNavData,
		}),
		[setUserSideNavData, userSideNavData],
	);

	return (
		<UserSideNavDataStoreContext.Provider value={userSideNavDataStore}>
			{children}
		</UserSideNavDataStoreContext.Provider>
	);
}

export function useUserSideNavDataStore() {
	const context = useContext(UserSideNavDataStoreContext);

	invariant(context, 'useUserSideNavDataStore must be used inside a UserSideNavDataStoreProvider');

	return context;
}
